<template>
  <div class="forbidden-to-change-plan p-24 planes">
    <div class="w100 overflow-auto">
      <div class="detailsTitle">
        {{ t('Subscription change to the selected plan is not available.') }}
      </div>
      <p>
        {{ t('What should be done:') }}
      </p>
      <p>
        {{
          t(
            'Archive the extra elements or/and delete previously configured additional options (such as domains, members, shared reports, custom columns and conversions) to meet the limits of the new plan.',
          )
        }}
      </p>
      <ui-table
        class="forbidden-plan-table mb-16 table-no-border-right"
        :headers="headers"
        :items="tableRows"
        :use-search="false"
        :show-pagination="false"
        :virtualize="false"
      />

      <ui-info color="red" class="mb-8">
        {{
          t(
            'Important: after the subscription change happens some of the previously activated options will no longer be active.',
          )
        }}<br />
        {{ t('The following options will be affected if configured:') }}<br />
        {{
          t(
            'advanced rules of traffic distribution (crawler, webview, proxy; referrer domain; uniqueness; visit cap, bot filter marks);',
          )
        }}<br />
        {{ t('bot filters;') }}<br />
        {{ t('double meta redirect;') }}<br />
        {{ t('traffic source params masking;') }}<br />
        {{
          t(
            'send postbacks, traffic loss %, uniqueness period on the level of campaign settings;',
          )
        }}<br />
        {{ t('landing page security;') }}<br />
        {{ t('tags;') }}<br />
        {{ t('marks;') }}<br />
        {{ t('smart rotation in campaign;') }}<br />
        {{ t('API cost collection with traffic sources.') }}
      </ui-info>

      <p
        v-html="
          t(
            `More details on what options will be limited for the new plan see in the <a href='{0}'>Plan Description</a> section.`,
            ['/app/profile/plan-description'],
          )
        "
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n, usePlanLimits } from '@/composables';
import { useStore } from '@/store';
import type { BillingPlan } from '@/services/models';

const props = defineProps<{
  plan: BillingPlan;
  usage?: Record<string, number | string | boolean>;
}>();

const { t } = useI18n();
const { planLimitsKeyTextShort } = usePlanLimits(undefined, false);
const store = useStore();

const tableRows = computed(() => {
  const usage = (props.usage ??
    (store.billing.usage as Record<string, number | string | boolean>) ??
    {}) as Record<string, number | string | boolean>;
  const limits = (props.plan?.limits ?? {}) as Record<string, number | string | boolean>;
  const labels = planLimitsKeyTextShort.value as Record<string, string>;

  return Object.keys(usage).reduce<
    {
      id: string;
      elements: string;
      current_amount: number | string | boolean;
      new_plan_limits: number | string | boolean;
    }[]
  >((acc, key) => {
    const currentAmount = usage[key];
    const newPlanLimit = limits[key];
    if (newPlanLimit !== undefined && +currentAmount > +newPlanLimit) {
      acc.push({
        id: key,
        elements: labels[key] ?? key,
        current_amount: currentAmount,
        new_plan_limits: newPlanLimit,
      });
    }
    return acc;
  }, []);
});

const headers = computed(() => [
  {
    text: t('Elements/ Options'),
    align: 'left' as const,
    sortable: false,
    value: 'elements',
  },
  {
    text: t('Current Amount'),
    align: 'center' as const,
    value: 'current_amount',
    sortable: false,
  },
  {
    text: t('New Plan Limits'),
    align: 'center' as const,
    value: 'new_plan_limits',
    sortable: false,
  },
]);
</script>

<style lang="scss" scoped>
.forbidden-to-change-plan {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-height: 100%;
}

.forbidden-plan-table {
  :deep(.table-header .table-cell:nth-child(1)),
  :deep(.table-body .table-row .table-cell:nth-child(1)) {
    width: 50%;
  }

  :deep(.table-header .table-cell:nth-child(2)),
  :deep(.table-body .table-row .table-cell:nth-child(2)) {
    padding-left: 5px !important;
    padding-right: 5px !important;
  }

  :deep(.table-body .table-row .table-cell:nth-child(1)) {
    text-align: left;
  }

  :deep(.table-body .table-row .table-cell:nth-child(2)),
  :deep(.table-body .table-row .table-cell:nth-child(3)) {
    text-align: center;
  }
}

.detailsTitle {
  font-size: 20px;
  line-height: 1.4;
  font-weight: bold;
  margin-bottom: 4px;
}
</style>
